/**
 * Sysadmin Catalog → Workflows hooks (PR-K1).
 *
 * Wraps `/api/v1/admin/platform/catalog/workflows/*`. Mirrors
 * `use-sysadmin-catalog-skills.ts` with publish-lifecycle helpers, but
 * with CLONE-relationship reads instead of assignments.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { apiClient } from './api-client';

// ─── Types ────────────────────────────────────────────────────────────────

export type WorkflowStatus = 'draft' | 'published' | 'deprecated' | 'retired';
export type CloneSyncStatus =
  | 'forked'
  | 'synced'
  | 'diverged'
  | 'out_of_date';

export interface WorkflowTriggerConfig {
  mode: 'conversational' | 'scheduled' | 'event';
  cron?: string;
  eventTypes?: string[];
  [key: string]: unknown;
}

export interface CatalogWorkflow {
  id: string;
  slug: string;
  displayName: string;
  description: string;
  definition: Record<string, unknown>;
  triggerConfig: Record<string, unknown>;
  inputSchema: Record<string, unknown>;
  outputSchema: Record<string, unknown>;
  referencedSkills: string[];
  referencedTools: string[];
  referencedFunctions: string[];
  category: string;
  version: number;
  isActive: boolean;
  status: WorkflowStatus;
  publishedAt: string | null;
  publishedBy: string | null;
  unpublishedAt: string | null;
  unpublishedBy: string | null;
  unpublishReason: string | null;
  cloneCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CatalogWorkflowCompositionSkill {
  id: string;
  slug: string;
  displayName: string;
  status: WorkflowStatus;
}

export interface CatalogWorkflowCompositionTool {
  id: string;
  slug: string;
  displayName: string;
  status: WorkflowStatus;
}

export interface CatalogWorkflowCompositionFunction {
  id: string;
  functionKey: string;
  displayName: string;
  isActive: boolean;
}

export interface CatalogWorkflowVersionEntry {
  id: string;
  version: number;
  changedAt: string;
  changedByAdmin: string | null;
}

export interface CatalogWorkflowDetail extends CatalogWorkflow {
  composition: {
    skills: CatalogWorkflowCompositionSkill[];
    tools: CatalogWorkflowCompositionTool[];
    functions: CatalogWorkflowCompositionFunction[];
    danglingSkillIds: string[];
    danglingToolIds: string[];
    danglingFunctionIds: string[];
  };
  versions: CatalogWorkflowVersionEntry[];
}

export interface CatalogWorkflowClone {
  id: string;
  tenantId: string;
  tenantName: string | null;
  sourceVersion: number;
  tenantWorkflowId: string;
  clonedAt: string;
  clonedBy: string | null;
  syncStatus: CloneSyncStatus;
  notes: string | null;
}

export interface CatalogWorkflowListResponse {
  items: CatalogWorkflow[];
  nextCursor: string | null;
}

const BASE = '/admin/platform';

const KEYS = {
  list: (filters: Record<string, unknown>) =>
    ['sysadmin-catalog-workflows', 'list', filters] as const,
  detail: (id: string) =>
    ['sysadmin-catalog-workflows', 'detail', id] as const,
  versions: (id: string) =>
    ['sysadmin-catalog-workflows', 'versions', id] as const,
  clones: (id: string) =>
    ['sysadmin-catalog-workflows', 'clones', id] as const,
};

// ─── List + detail ────────────────────────────────────────────────────────

export interface UseCatalogWorkflowsParams {
  status?: WorkflowStatus;
  category?: string;
  search?: string;
  limit?: number;
}

export function useCatalogWorkflows(params: UseCatalogWorkflowsParams = {}) {
  return useInfiniteQuery<CatalogWorkflowListResponse, Error>({
    queryKey: KEYS.list(params as Record<string, unknown>),
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.category) qs.set('category', params.category);
      if (params.search) qs.set('search', params.search);
      if (params.limit) qs.set('limit', String(params.limit));
      if (pageParam) qs.set('cursor', String(pageParam));
      const query = qs.toString() ? `?${qs.toString()}` : '';
      return apiClient.get<CatalogWorkflowListResponse>(
        `${BASE}/catalog/workflows${query}`,
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function useCatalogWorkflow(id: string | null | undefined) {
  return useQuery<{ data: CatalogWorkflowDetail }, Error>({
    queryKey: id ? KEYS.detail(id) : ['__skip__'],
    queryFn: () =>
      apiClient.get<{ data: CatalogWorkflowDetail }>(
        `${BASE}/catalog/workflows/${id}`,
      ),
    enabled: !!id,
  });
}

export function useCatalogWorkflowClones(id: string | null | undefined) {
  return useQuery<{ items: CatalogWorkflowClone[] }, Error>({
    queryKey: id ? KEYS.clones(id) : ['__skip__'],
    queryFn: () =>
      apiClient.get<{ items: CatalogWorkflowClone[] }>(
        `${BASE}/catalog/workflows/${id}/clones`,
      ),
    enabled: !!id,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────

export interface CreateCatalogWorkflowInput {
  slug: string;
  displayName: string;
  description: string;
  definition?: Record<string, unknown>;
  triggerConfig?: WorkflowTriggerConfig;
  inputSchema?: Record<string, unknown>;
  outputSchema?: Record<string, unknown>;
  referencedSkills?: string[];
  referencedTools?: string[];
  referencedFunctions?: string[];
  category?: string;
}

export function useCreateCatalogWorkflow() {
  const qc = useQueryClient();
  return useMutation<
    { data: CatalogWorkflow },
    Error,
    CreateCatalogWorkflowInput
  >({
    mutationFn: (body) =>
      apiClient.post<{ data: CatalogWorkflow }>(
        `${BASE}/catalog/workflows`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-workflows'] });
    },
  });
}

export interface UpdateCatalogWorkflowInput
  extends Partial<Omit<CreateCatalogWorkflowInput, 'slug'>> {
  id: string;
}

export function useUpdateCatalogWorkflow() {
  const qc = useQueryClient();
  return useMutation<
    { data: CatalogWorkflow },
    Error,
    UpdateCatalogWorkflowInput
  >({
    mutationFn: ({ id, ...body }) =>
      apiClient.patch<{ data: CatalogWorkflow }>(
        `${BASE}/catalog/workflows/${id}`,
        body,
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-workflows'] });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
}

export function useDeleteCatalogWorkflow() {
  const qc = useQueryClient();
  return useMutation<{ success: true }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete<{ success: true }>(`${BASE}/catalog/workflows/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-workflows'] });
    },
  });
}

export function usePublishCatalogWorkflow() {
  const qc = useQueryClient();
  return useMutation<{ data: CatalogWorkflow }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post<{ data: CatalogWorkflow }>(
        `${BASE}/catalog/workflows/${id}/publish`,
        {},
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-workflows'] });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
}

export interface UnpublishCatalogWorkflowInput {
  id: string;
  reason: string;
  nextStatus?: 'draft' | 'deprecated' | 'retired';
}

export function useUnpublishCatalogWorkflow() {
  const qc = useQueryClient();
  return useMutation<
    { data: CatalogWorkflow },
    Error,
    UnpublishCatalogWorkflowInput
  >({
    mutationFn: ({ id, reason, nextStatus }) =>
      apiClient.post<{ data: CatalogWorkflow }>(
        `${BASE}/catalog/workflows/${id}/unpublish`,
        { reason, nextStatus: nextStatus ?? 'deprecated' },
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-workflows'] });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
}
